import React from 'react';
import styled from 'styled-components';
import {Redirect, useLocation} from 'react-router-dom';
import {Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {canOpenReportBuilder} from './reportBuilderGate';
import {ReportBuilderProvider, ReportIntentPayload} from './ReportBuilderContext';
import {ReportBuilderSurface} from './ReportBuilderSurface';
import {validateReportDefinition} from './contracts/validator';
import {ReportDataSource} from './data/ReportDataSource';
import {HttpReportDataSource} from './data/HttpReportDataSource';

/** Production data source: the real `POST /report/data/fetch` transport. A module singleton so a
 * re-render does not churn a new instance (keeps the provider's memoised source stable).
 * Tests/Storybook inject a `MockReportDataSource` via the `dataSource` prop instead. */
const httpReportDataSource: ReportDataSource = new HttpReportDataSource();

/** Report intent handed to the page through react-router location state when the chat card's "View"
 * navigates here. The intent is ephemeral (agent-generated, not persisted), so a refresh or direct
 * hit carries no state and the page redirects away. */
interface ReportBuilderLocationState {
	initialConfig?: ReportIntentPayload;
	/** Logged-in person id, scopes the per-user layout overlay. Falls back to the provider's 'me'. */
	userId?: string;
}

/** Fills the routed content area as a normal page (in-flow, not a portalled full-viewport takeover).
 * The surface itself scrolls. */
const Surface = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: auto;
`;

const ErrorPanel = styled.div`
	padding: 24px;
`;

export interface ReportBuilderPageProps {
	/** Overridable for tests/Storybook; production defaults to the live `HttpReportDataSource`. */
	dataSource?: ReportDataSource;
}

/** The AI report builder rendered as a routed view (path `/report-builder`), mounted in the app's
 * main content region beside the primary navigation. Reached only from the chat card's "View", which
 * navigates here with the report intent in location state. Security-critical gate: redirects to `/`
 * unless `canOpenReportBuilder()` passes AND an intent is present in location state. Guards a
 * malformed `initialConfig.definition` (e.g. an upstream agent response that fails schema
 * validation) by rendering an error block instead of crashing the surface/provider. */
export function ReportBuilderPage({dataSource = httpReportDataSource}: ReportBuilderPageProps): JSX.Element {
	const {formatMessage} = useIntl();
	const location = useLocation<ReportBuilderLocationState>();
	const state = location.state;

	if (!canOpenReportBuilder() || !state || !state.initialConfig) {
		return <Redirect to="/" />;
	}

	const {initialConfig, userId} = state;
	const {valid, errors} = validateReportDefinition(initialConfig.definition);

	if (!valid) {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Text>{formatMessage({id: 'ai_report_builder.error_title'})}</Text>
				<Text>{formatMessage({id: 'ai_report_builder.error_invalid_definition'})}</Text>
				<Text>{errors.join(', ')}</Text>
			</ErrorPanel>
		);
	}

	return (
		<Surface data-cy="ai-report-builder-page">
			{/* Key by draftId so a refined create_report_intent (new draftId) remounts the provider with
			    the new definition. The provider seeds definition/grouping/tableState once on mount with
			    no reset-on-payload, so without a changing key a refinement would keep the previous
			    draft. Falls back to a stable key for non-intent opens. */}
			<ReportBuilderProvider
				key={initialConfig.draftId ?? 'report-builder'}
				payload={initialConfig}
				role={'editor'}
				dataSource={dataSource}
				userId={userId}
			>
				<ReportBuilderSurface />
			</ReportBuilderProvider>
		</Surface>
	);
}

export default ReportBuilderPage;
